import React, { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { forgotPasswordSchema } from "@/utils/schemaHelper";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/uiComponents/components/ui/form";
import { Input } from "@/uiComponents/components/ui/input";
import { useNavigate } from "react-router-dom";
import { forgotPassword } from "@/modules/authenticationService";
import { ToastSuccessMessages } from "@/enums/errorMessages";
import { ROUTES } from "@/enums/routes";
import { TiTick } from "react-icons/ti";

import { useDispatch, useSelector } from "react-redux";

import { RootState } from "@/store/store";
import { setLoading } from "@/store/redux/appSlice";
import { ErrorHelper } from "@/utils/errorHelper";
import { toastMessage } from "@/utils/toastHelper";
import { Profile } from "@/types/authentication";
import { ReusableButton } from "@/reusable/reusableButton";

const ForgotPassword = () => {
  const dispatch = useDispatch();
  const user = useSelector(
    (state: RootState) => state.auth.profile,
  ) as Profile | null;
  const loading = useSelector((state: RootState) => state.app.loading);

  const navigate = useNavigate();
  const form = useForm<z.infer<typeof forgotPasswordSchema>>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: "onChange",
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof forgotPasswordSchema>) => {
    if (loading) return;

    dispatch(setLoading(true));

    try {
      await forgotPassword(values);

      navigate(ROUTES.LOGIN);

      toastMessage("success", ToastSuccessMessages.FORGOT_PASSWORD_EMAIL_SENT);
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    } finally {
      dispatch(setLoading(false));
    }
  };
  useEffect(() => {
    if (user) {
      navigate(ROUTES.DASHBOARD);
    }
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center w-screen bg-gray-100 px-4 ">
      <div className="w-full max-w-md bg-white p-8 rounded-lg shadow-lg">
        <h2 className="text-center text-2xl font-semibold mb-2 text-gray-800">
          Forgot Password
        </h2>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 ">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="max-h-13 ">
                  <FormLabel>Email</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input
                        {...field}
                        className={`${
                          field.value && !form.formState.errors.email
                            ? "border-green-500 transition-all focus-visible:ring-green-200  "
                            : ""
                        }`}
                      />
                    </FormControl>
                    <div className="absolute right-2 top-2">
                      {field.value && !form.formState.errors.email && (
                        <TiTick className="text-green-700" />
                      )}
                    </div>
                  </div>

                  <div className="min-h-6">
                    <FormMessage className="text-xs" />
                  </div>
                </FormItem>
              )}
            />

            <div className="flex justify-center ">
              <ReusableButton
                label="Request"
                loading={loading}
                className="w-full"
              />
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};
export default ForgotPassword;
